import React, { createContext, useState, useContext, useEffect } from 'react';
import { STORAGE_KEYS } from '../config/constants';

// Create the cart context
export const CartContext = createContext();

// Cart provider component
export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load cart from localStorage on startup
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem(STORAGE_KEYS.CART_ITEMS);
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        setCart(parsedCart);
        console.log('[CartContext] Loaded cart from storage:', parsedCart.length, 'items');
      }
    } catch (error) {
      console.error('[CartContext] Error loading cart from storage:', error);
      setCart([]);
    }
  }, []);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CART_ITEMS, JSON.stringify(cart));
      console.log('[CartContext] Cart saved to storage:', cart.length, 'items');
    } catch (error) {
      console.error('[CartContext] Error saving cart to storage:', error);
    }
  }, [cart]);

  // Add item to cart
  const addToCart = (item) => {
    try {
      console.log('[CartContext] Adding item to cart:', item);
      
      // Generate unique ID for cart item if not provided
      const cartItem = {
        ...item,
        id: item.id || Date.now().toString(),
        addedAt: new Date().toISOString()
      };

      setCart(prevCart => {
        const newCart = [...prevCart, cartItem];
        console.log('[CartContext] New cart total items:', newCart.length);
        return newCart;
      });

      return true;
    } catch (error) {
      console.error('[CartContext] Error adding item to cart:', error);
      return false;
    }
  };

  // Remove item from cart
  const removeFromCart = (itemId) => {
    try {
      console.log('[CartContext] Removing item from cart:', itemId);
      
      setCart(prevCart => {
        const newCart = prevCart.filter(item => item.id !== itemId);
        console.log('[CartContext] Items remaining in cart:', newCart.length);
        return newCart;
      });

      return true;
    } catch (error) {
      console.error('[CartContext] Error removing item from cart:', error);
      return false;
    }
  };

  // Update item quantity in cart
  const updateCartItemQuantity = (itemId, newQuantity) => {
    try {
      if (newQuantity <= 0) {
        return removeFromCart(itemId);
      }

      console.log('[CartContext] Updating item quantity:', itemId, 'to', newQuantity);
      
      setCart(prevCart => {
        return prevCart.map(item => {
          if (item.id === itemId) {
            const updatedItem = { 
              ...item, 
              quantity: newQuantity,
              // Recalculate total based on new quantity
              total: (item.total / item.quantity) * newQuantity
            };
            return updatedItem;
          }
          return item;
        });
      });

      return true;
    } catch (error) {
      console.error('[CartContext] Error updating item quantity:', error);
      return false;
    }
  };

  // Clear entire cart
  const clearCart = () => {
    try {
      console.log('[CartContext] Clearing cart');
      setCart([]);
      return true;
    } catch (error) {
      console.error('[CartContext] Error clearing cart:', error);
      return false;
    }
  };

  // Get cart total amount
  const getCartTotal = () => {
    try {
      const total = cart.reduce((sum, item) => {
        return sum + (item.total || 0);
      }, 0);
      
      return Number(total.toFixed(2));
    } catch (error) {
      console.error('[CartContext] Error calculating cart total:', error);
      return 0;
    }
  };

  // Get cart subtotal (before taxes/fees)
  const getCartSubtotal = () => {
    try {
      const subtotal = cart.reduce((sum, item) => {
        // If item has a subtotal field, use it; otherwise use total
        const itemSubtotal = item.subtotal || item.total || 0;
        return sum + itemSubtotal;
      }, 0);
      
      return Number(subtotal.toFixed(2));
    } catch (error) {
      console.error('[CartContext] Error calculating cart subtotal:', error);
      return 0;
    }
  };

  // Get total number of items in cart
  const getCartItemCount = () => {
    try {
      return cart.reduce((count, item) => {
        return count + (item.quantity || 1);
      }, 0);
    } catch (error) {
      console.error('[CartContext] Error calculating cart item count:', error);
      return 0;
    }
  };

  // Check if item exists in cart
  const isItemInCart = (itemId) => {
    try {
      return cart.some(item => item.id === itemId);
    } catch (error) {
      console.error('[CartContext] Error checking if item is in cart:', error);
      return false;
    }
  };

  // Get specific cart item
  const getCartItem = (itemId) => {
    try {
      return cart.find(item => item.id === itemId) || null;
    } catch (error) {
      console.error('[CartContext] Error getting cart item:', error);
      return null;
    }
  };

  // Format cart for order submission
  const formatCartForOrder = () => {
    try {
      return cart.map(item => ({
        menuItemId: item.drink?.id || item.menuItemId,
        menuItemName: item.drink?.name || item.name,
        quantity: item.quantity || 1,
        size: item.size,
        toppings: item.toppings || [],
        itemPrice: item.total / (item.quantity || 1),
        totalPrice: item.total,
        notes: item.notes || ''
      }));
    } catch (error) {
      console.error('[CartContext] Error formatting cart for order:', error);
      return [];
    }
  };

  // Validate cart before checkout
  const validateCart = () => {
    try {
      if (cart.length === 0) {
        return { valid: false, error: 'Cart is empty' };
      }

      for (const item of cart) {
        if (!item.drink?.id && !item.menuItemId) {
          return { valid: false, error: 'Invalid item in cart' };
        }
        
        if (!item.quantity || item.quantity <= 0) {
          return { valid: false, error: 'Invalid quantity in cart' };
        }
        
        if (!item.total || item.total <= 0) {
          return { valid: false, error: 'Invalid item price in cart' };
        }
      }

      return { valid: true };
    } catch (error) {
      console.error('[CartContext] Error validating cart:', error);
      return { valid: false, error: 'Cart validation failed' };
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    getCartTotal,
    getCartSubtotal,
    getCartItemCount,
    isItemInCart,
    getCartItem,
    formatCartForOrder,
    validateCart,
    setLoading
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 